/**
 * Guest Prompt
 *
 * The nudge to turn a guest session into a real account. Sits at the bottom of
 * the sidebar, directly above the account button — the one piece of chrome
 * that's on screen no matter which tool the person is using, and right next to
 * the identity it's talking about.
 *
 * Renders for guests only. The moment the account is claimed it disappears for
 * good, so it can afford to be persistent rather than dismissible: it's the
 * only thing standing between someone and losing the leagues they just
 * connected, which is worth being direct about.
 *
 * Deliberately states the actual stake ("keep what you've set up") rather than
 * a generic "Sign up!". A guest account lives entirely in this browser's
 * storage — clearing site data or switching device loses it — so the honest
 * reason to claim it is also the most persuasive one.
 */

import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useLeagues } from '../context/LeagueContext'
import { JAI_COLORS, JAI_FONTS } from './JaiBrand'

export default function GuestPrompt() {
  const { isGuest } = useAuth()
  const { leagues } = useLeagues()
  const navigate = useNavigate()

  if (!isGuest) return null

  // Once they've actually connected something, name it — "your league" is a
  // concrete thing to lose, where "your progress" is an abstraction.
  const stake = leagues.length > 0
    ? `Save ${leagues.length === 1 ? 'your league' : 'your leagues'}, notes and chat history before you lose them.`
    : 'Create a free account to save your leagues, notes and chat history.'

  return (
    <div
      style={{
        borderLeft: '3px solid rgba(58,124,255,0.9)',
        borderRadius: 0,
        padding: '2px 0 4px 12px',
        marginBottom: 10,
      }}
    >
      <div style={{ fontFamily: JAI_FONTS.tight, fontSize: 13.5, fontWeight: 700, color: 'white', letterSpacing: '-0.01em' }}>
        You're browsing as a guest
      </div>
      <p style={{ margin: '4px 0 10px', fontFamily: JAI_FONTS.tight, fontSize: 12.5, lineHeight: 1.45, color: 'rgba(246,248,251,0.72)' }}>
        {stake}
      </p>
      <button
        type="button"
        onClick={() => navigate('/signup')}
        style={{
          width: '100%',
          background: JAI_COLORS.blueBright,
          border: 'none',
          borderRadius: 9,
          padding: '8px 12px',
          color: 'white',
          fontFamily: JAI_FONTS.tight,
          fontSize: 13.5,
          fontWeight: 650,
          letterSpacing: '-0.01em',
          cursor: 'pointer',
          transition: 'filter 150ms',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.filter = 'brightness(1.12)' }}
        onMouseLeave={(e) => { e.currentTarget.style.filter = 'none' }}
      >
        Create your free account
      </button>
    </div>
  )
}
